import { memo, PropsWithChildren, useState, MouseEvent, useRef } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider, Box, Typography } from '@mui/material';

import { MapActiveLayer, WMSLayer, WMSInfo } from '../../Map.types';
import { LayerToggle } from '../LayerToggle';
import IS_EFAS from '../../../../components/sitebase/Environment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as frStar } from '@fortawesome/free-regular-svg-icons';
import { WMSListPresentation } from './WMSList.types';


interface FavouriteGroupI {
    baseWms: WMSInfo,
    layers: { group: string, layers: WMSLayer[] }[],
    activeLayers: MapActiveLayer[],
    favourites: Set<string>,
    setFavouriteLayer: (layerId: string) => void,
    presentation: WMSListPresentation,
    anonymousUser: boolean,
    forecastEmbargoActive: boolean,
    forecastDate: string,
    addLayer: (source: string, group: string, layer: WMSLayer) => void
}

export default memo(function FavouriteGroup(props: PropsWithChildren<FavouriteGroupI>) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const touchRef = useRef(false);
    const [backdropOpen, setBackdropOpen] = useState(false);

    const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
        if (touchRef.current) {
            touchRef.current = false;
            return;
        }
        setBackdropOpen(false);
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
        setAnchorEl(event.currentTarget);
    };

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        touchRef.current = false;
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
        if (anchorEl) {
            setAnchorEl(null);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        closeTimeout.current = setTimeout(() => {
            setAnchorEl(null);
        }, 0);
    };

    const handleMenuEnter = () => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
    };

    return (
        <>
            <li key="Favourites" id="favourites" className='list-group-item efas-wms-layer-menu'>
                <Box
                    onClick={handleClick}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleClose}
                    sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '2.5rem',
                        padding: '0.5rem',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        }
                    }}
                    aria-haspopup="true"
                    aria-expanded={open}
                >
                    <FontAwesomeIcon icon={faStar} style={{ fontSize: '1rem' }} />
                </Box>

                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    transitionDuration={0}
                    disableAutoFocusItem={true}
                    disableScrollLock={true}
                    disableRestoreFocus={true}
                    hideBackdrop={!backdropOpen}
                    sx={{ pointerEvents: backdropOpen ? 'auto' : 'none' }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    slotProps={{
                        paper: {
                            onMouseEnter: handleMenuEnter,
                            onMouseLeave: handleClose,
                            sx: {
                                maxHeight: '75vh',
                                minWidth: '250px',
                                pointerEvents: 'auto'
                            }
                        }
                    }}
                >
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography variant="subtitle1" align="center" sx={{ fontWeight: 500 }}>
                            Favourites
                        </Typography>
                        {/* <Button variant="contained" color="warning" size="small" fullWidth sx={{ mt: 1 }}>
                            Add all
                        </Button> */}
                    </Box>
                    <Divider />

                    {props.layers.map(entry => {
                        return entry.layers.map(layer => {
                            const toggled = props.activeLayers.find((l) => l.name === layer.Name && l.source === props.baseWms?.url) ? true : false;
                            const allowed_for_public = !props.anonymousUser || !props.forecastEmbargoActive || (IS_EFAS && layer.Keywords && layer.Keywords["public"] === "true" ? true : false);

                            const toggleAction = !allowed_for_public ?
                                () => { } :
                                () => {
                                    props.addLayer(props.baseWms!.url, entry.group, layer);
                                };

                            return (
                                <MenuItem
                                    key={layer.Name + toggled}
                                    data-layer-name={layer.Name}
                                    data-layer-source={props.baseWms?.name}
                                    onClick={toggleAction}
                                    disabled={!allowed_for_public}
                                    sx={{
                                        py: 1,
                                        px: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                                        <LayerToggle toggled={toggled} id={layer.Name} disabled={!allowed_for_public} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={layer.Title}
                                        slotProps={{
                                            primary: {
                                                variant: 'body1',
                                                sx: { flex: 1 }
                                            }
                                        }}
                                    />
                                    <Box
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            props.setFavouriteLayer(layer.Title);
                                        }}
                                        sx={{
                                            ml: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            className={props.favourites.has(layer.Title) ? 'text-warning' : ''}
                                            icon={props.favourites.has(layer.Title) ? faStar : frStar}
                                        />
                                    </Box>
                                </MenuItem>
                            );
                        });
                    })}
                </Menu>
            </li>
        </>
    );
});